var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import React, { useEffect, useRef, useState } from 'react';
import { Field } from 'react-final-form';
import { StyledInputLabel, InputElementHeading } from '../elements';
import { Input } from '../input/input';
import { adaptValueToFormat, fixToDecimals, validKeys as vk } from '../helpers';
import AccessibilityHelper from '../accessibilityHelper';
var numberFieldI18nDefaults = {
    ariaLabelExtension: null,
};
var InputNumber = function (_a) {
    var id = _a.id, label = _a.label, name = _a.name, hint = _a.hint, input = _a.input, testId = _a.testId, meta = _a.meta, required = _a.required, placeholder = _a.placeholder, readOnly = _a.readOnly, width = _a.inputWidth, cfg = _a.cfg, after = _a.after, before = _a.before, callback = _a.callback, decimalPlaces = _a.decimalPlaces, noLeftBorder = _a.noLeftBorder, optionalText = _a.optionalText, maxLength = _a.maxLength, maxIntDigits = _a.maxIntDigits, _b = _a.i18n, i18n = _b === void 0 ? numberFieldI18nDefaults : _b, initialValue = _a.initialValue, props = __rest(_a, ["id", "label", "name", "hint", "input", "testId", "meta", "required", "placeholder", "readOnly", "inputWidth", "cfg", "after", "before", "callback", "decimalPlaces", "noLeftBorder", "optionalText", "maxLength", "maxIntDigits", "i18n", "initialValue"]);
    var digits = [
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '.',
        '-',
        '+',
    ];
    var validKeys = __spreadArrays(vk, digits);
    var _c = useState(null), prevValue = _c[0], setPrevValue = _c[1];
    var innerInput = useRef(null);
    var setInitialDisplayValue = function (initialDisplayValue) {
        var formattedInitialValue = fixToDecimals(initialDisplayValue.toString(), decimalPlaces);
        if (innerInput.current.value === formattedInitialValue) {
            return;
        }
        innerInput.current.value = formattedInitialValue;
        setTimeout(function () {
            innerInput.current.dispatchEvent(new Event('blur', { bubbles: true }));
        }, 100);
    };
    useEffect(function () {
        if (innerInput.current.defaultValue === undefined ||
            innerInput.current.defaultValue === null ||
            innerInput.current.defaultValue === '') {
            return;
        }
        setInitialDisplayValue(innerInput.current.defaultValue);
    }, [innerInput.current]);
    var reachedMaxIntDigits = function (value) {
        var newInt = parseInt(value);
        return Math.abs(newInt).toString().length > maxIntDigits ? true : false;
    };
    var handleKeyDown = function (e) {
        if (!(e.ctrlKey === true)) {
            e.key.toLowerCase() === 'e' && e.preventDefault();
            !validKeys.includes(e.key) && e.preventDefault();
            e.key === '.' && !decimalPlaces && e.preventDefault();
        }
    };
    var valueLengthValid = function (value) {
        if (value.length > maxLength) {
            return false;
        }
        return true;
    };
    var handleOnChange = function (e) {
        var newEvent = __assign({}, e);
        if (e.target.value !== '' && e.target.value !== '-') {
            decimalPlaces
                ?
                    (newEvent.target.value =
                        e.target.value &&
                            adaptValueToFormat(newEvent.target.value, decimalPlaces))
                :
                    (newEvent.target.value =
                        e.target.value && parseInt(newEvent.target.value, 10).toString());
        }
        !valueLengthValid(newEvent.target.value) &&
            (newEvent.target.value = prevValue);
        reachedMaxIntDigits(newEvent.target.value)
            ?
                (newEvent.target.value = prevValue)
            :
                setPrevValue(newEvent.target.value);
        input.onChange(newEvent.target.value);
        callback && callback(newEvent);
    };
    var handleBlur = function (e) {
        var newValue = fixToDecimals(e.target.value, decimalPlaces);
        if (e.target.value)
            e.target.value = newValue;
        input.onChange(e.target.value);
        input.onBlur(e.target.value);
    };
    var helper = new AccessibilityHelper(name, !!label, !!hint);
    return (React.createElement(StyledInputLabel, { isError: meta && meta.touched && meta.error, cfg: Object.assign({ flexDirection: 'column', mt: 1 }, cfg), noLeftBorder: noLeftBorder },
        React.createElement(InputElementHeading, { label: label, required: optionalText !== undefined ? !optionalText : required, hint: hint, meta: meta, accessibilityHelper: helper }),
        React.createElement(Input, __assign({ id: id, name: name, type: "number", width: width, testId: testId, label: label, isError: meta && meta.touched && meta.error, placeholder: placeholder, readOnly: readOnly, decimalPlaces: decimalPlaces }, input, { onKeyDown: handleKeyDown, onChange: handleOnChange, onBlur: handleBlur, after: after, before: before, ariaLabelExtension: i18n.ariaLabelExtension, accessibilityHelper: helper, parentRef: innerInput }, props))));
};
export var FFInputNumber = function (fieldProps) {
    return (React.createElement(Field, __assign({}, fieldProps, { render: function (props) { return (React.createElement(InputNumber, __assign({}, props, { name: fieldProps.name, initialValue: fieldProps.initialValue }))); } })));
};
//# sourceMappingURL=number.js.map